import React, { useState, useEffect, useRef } from 'react';
import { load as loadMyStatus, forgetMyStatus } from './lib/chatStatus';
import { createBrowserRouter, RouterProvider, Routes, Route, Link, NavLink, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
/* Dashboard, TicketsList, ApprovalsPage, WorkOrdersList, SLATrackingDashboard,
   AdminAnalyticsDashboard, ContractorsList, AdvancedSearch and TicketTemplates
   are no longer mounted here: they are the tabs of the Ticket Mgmt hub
   (pages/ManageHub.js), which imports them. */
import CreateTicket from './pages/CreateTicket';
import TicketDetail from './pages/TicketDetail';
import Settings from './pages/Settings';
import UserDashboard from './pages/UserDashboard';
import ContractorDashboard from './pages/ContractorDashboard';
import ContractorProfile from './pages/ContractorProfile';
import Login from './pages/Login';
import AdminAuditLog from './pages/AdminAuditLog';
import RollCall from './pages/RollCall';
import WorkOrderDetail from './pages/WorkOrderDetail';
import ContractorTicket from './pages/ContractorTicket';
import ConversationTab from './components/ticket/ConversationTab';
import WorkTab from './components/ticket/WorkTab';
import { DeviceTab, DocsTab, FilesTab, HistoryTab } from './components/ticket/OtherTabs';
import AssetOverview from './components/asset/AssetTabs/Overview';
import AssetHistory from './components/asset/AssetTabs/History';
import AssetTickets from './components/asset/AssetTabs/Tickets';
import AssetDevice from './components/asset/AssetTabs/Device';
import AssetNotes from './components/asset/AssetTabs/Notes';
import AssetFiles from './components/asset/AssetTabs/Files';
import AssetAccess from './components/asset/AssetTabs/Access';
import ContractorCreateTicket from './pages/ContractorCreateTicket';
import ContractorRequests from './pages/ContractorRequests';
import RequestCatalogue from './pages/catalogue/RequestCatalogue';
import MyRequests from './pages/catalogue/MyRequests';
import SessionPage from './pages/SessionPage';
import ChatPage from './pages/ChatPage';
import ChatArchive from './pages/ChatArchive';
import AssetsPage from './pages/AssetsPage';
import AssetDetail from './pages/AssetDetail';
import AssetLabels from './pages/AssetLabels';
import AssetDiscovery from './pages/AssetDiscovery';
import AssetDiscoveryCleanup from './pages/AssetDiscoveryCleanup';
import AssetDiscoveryApprove from './pages/AssetDiscoveryApprove';
import AssetStocktake from './pages/AssetStocktake';
import AssetLookup from './pages/AssetLookup';
import AssetPlace from './pages/AssetPlace';
import AssetPlaceLabels from './pages/AssetPlaceLabels';
import AssetDevices from './pages/AssetDevices';
import AssetFleet from './pages/AssetFleet';
import AssetMap from './pages/AssetMap';
import AssetDesk from './pages/AssetDesk';
import AssetLeavers from './pages/AssetLeavers';
import LicencesList from './pages/licences/LicencesList';
import LicenceDetail from './pages/licences/LicenceDetail';
import LicencePerson from './pages/licences/LicencePerson';
import AnnouncementsPage from './pages/announcements/AnnouncementsPage';
import AnnouncementDetail from './pages/announcements/AnnouncementDetail';
import AnnouncementBanner from './components/announcements/AnnouncementBanner';
import PeoplePage from './pages/PeoplePage';
import MyEquipment from './pages/MyEquipment';
import TagLanding from './pages/TagLanding';
import ManageHub from './pages/ManageHub';
import NotFound from './pages/NotFound';
import InstallPage from './pages/InstallPage';
import NotificationSettings from './pages/NotificationSettings';
import RecordingsPage from './components/recording/RecordingsPage';
import RecordingDetail from './components/recording/RecordingDetail';
import MeetingsPage from './components/meetings/MeetingsPage';
import MeetingPage from './components/meetings/MeetingPage';
import GuestJoinPage from './components/meetings/GuestJoinPage';
import RoomRecorder from './components/meetings/RoomRecorder';
import { ChatNavItem, ChatIconLink } from './components/ChatLink';
import ContractorAvailability from './components/ContractorAvailability';
import TodoApp from './components/todos/TodoApp';
import LinksPage from './pages/LinksPage';
import DocsPage from './components/docs/DocsPage';
import { useDocsMe } from './lib/docs';
import HelpPage from './pages/help/HelpPage';
import { useHelpMe } from './lib/help';
import IncomingCall from './components/IncomingCall';
import ReminderAlerts from './components/todos/ReminderAlerts';
import LocationGate from './components/LocationGate';
import FeatureRoute from './components/FeatureRoute';
import { useFeatures } from './lib/features';

import NotificationsBell from './components/NotificationsBell';
import { contractorToken, contractorAuth, portalContractor, contractorIdentity } from './lib/contractorAuth';
import { rememberReturnPath, takeReturnPath } from './lib/returnPath';
import Logo, { LogoMark } from './components/Logo';
import ThemeToggle from './components/ThemeToggle';
import SignIn, { ServerReachNote } from './components/SignIn';
import ErrorBoundary from './components/ErrorBoundary';
import DegradedBanner from './components/DegradedBanner';
import { useMe } from './lib/me';
import { useBlocked } from './lib/blocked';
import AccessSwitchedOff from './components/AccessSwitchedOff';
import { firstName, rememberedSignIn } from './lib/signInMemory';
import { useAssetMeta, myEquipmentRequest } from './lib/assets';
import ViewAsBar from './components/ViewAsBar';
import GlobalSearch, { SearchButton } from './components/GlobalSearch';
import { setViewAsAllowed, storedViewAs, viewingContractorId, clearViewAs }
  from './lib/viewAs';
import './App.css';

/**
 * Availability on its own page.
 *
 * The component wants the contractor's id and token, which the portal used to
 * hand it from its own state. Read here from the same two localStorage keys
 * the portal reads - through lib/contractorAuth, which wraps them, because a
 * plain localStorage.getItem throws rather than returning null in a private
 * window and this is now a route somebody can land on directly.
 */
function ContractorAvailabilityPage() {
  // portalContractor() rather than contractorIdentity(): the second answers
  // only from a portal TOKEN, so an administrator using "View as" had no id
  // and was bounced to the contractor login - being asked for somebody else's
  // credentials to look at a page the API would have given them. The
  // availability endpoints are behind optionalContractorSelf, which admits an
  // administrator by name, so the id is all that was missing.
  const who = portalContractor();
  if (!who?.id) return <ToLogin />;
  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">Your availability</h1>
        <p className="page-description">
          When you can normally work, and the days you cannot.
        </p>
      </div>
      <ContractorAvailability contractorId={who.id} token={contractorToken()} />
    </div>
  );
}

/**
 * The to-do list on its own page.
 *
 * It began as a sixth portal tab. Gordon: "Move To Do on the top bar in
 * between Chat and availability" - the same reasoning that moved Availability
 * out of the tabs: the tabs are views of work in flight, and a to-do list is
 * about the contractor's day rather than any one job. Beside Chat and
 * Availability because all three are things done ABOUT the work.
 *
 * The ticket picker inside wants the contractor's assignments, which the
 * portal page used to hand it from its own state; here they are fetched once.
 */
function ContractorTodoPage() {
  const who = portalContractor();
  const [tickets, setTickets] = useState([]);
  useEffect(() => {
    if (!who?.id) return undefined;
    let live = true;
    // The contractor's own token when they hold one; otherwise nothing, and
    // the axios interceptor attaches the administrator's Entra token instead.
    // /contractors/:id/assignments is behind optionalContractorSelf, which
    // admits either - so one call works for both viewers.
    axios.get(`${process.env.REACT_APP_API_URL}/contractors/${who.id}/assignments`,
      contractorToken() ? { headers: { Authorization: `Bearer ${contractorToken()}` } } : undefined)
      .then((r) => {
        if (!live) return;
        const d = r.data;
        setTickets(Array.isArray(d) ? d : (d?.assignments ?? []));
      })
      .catch(() => { if (live) setTickets([]); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who?.id]);
  if (!who?.id) return <ToLogin />;
  return (
    <div className="container">
      {/* On a phone the app bar already says where we are (Oct 2026). */}
      <div className="page-header tda-hide-phone">
        <h1 className="page-title">To do</h1>
        <p className="page-description">
          The small things a ticket does not carry: a part to order, a call to
          return, a tool to bring.
        </p>
      </div>
      {/* With no portal token - an administrator using View as - TodoApp
          names the contractor on every call (as=c:<id>), because the staff
          token on its own now means the administrator's own list. */}
      <TodoApp owner={{ kind: 'contractor', id: who.id }} token={contractorToken()} tickets={tickets} />
    </div>
  );
}

/**
 * Everybody else's to-do list: staff and administrators.
 *
 * Gordon decided the to-do list is for EVERYONE, not only contractors - the
 * same Todoist-style lists, sections and labels for a member of staff as for
 * a contractor, each person's their own. The staff token says whose; the
 * tickets it can pin to are whatever that person may read, found by search
 * (TodoApp asks /todos/tickets) rather than handed in.
 */
const MY_TODOS = { kind: 'self' };
function StaffTodoPage() {
  return (
    <div className="container">
      {/* A compact header: the open list or view names itself right below,
          so this is only where-am-I, not a paragraph (TodoApp.css, .tda-page-head). */}
      <div className="page-header tda-page-head">
        <h1 className="page-title">To do</h1>
      </div>
      <TodoApp owner={MY_TODOS} />
    </div>
  );
}

/*
 * "Create Ticket" on the contractor's bar, 21 October, under Tickets beside
 * My Tickets, opened a placeholder for a day: the server took no ticket from
 * a portal token. Gordon settled it on the 22nd - "yes allow them to raise
 * tickets as well - they might need subscriptions or to buy things for the
 * company" - so /create for a contractor, and for an administrator looking
 * through View as, raises a request to the firm that goes through the same
 * approval as a staff ticket (migration 064). Since the 26th - "why don't we
 * have the standard ticket here? It's more open" - it is the standard form
 * (pages/CreateTicket.js) in the portal's frame (ContractorCreateTicket),
 * device picker included.
 */

/*
 * The addresses the Ticket Mgmt hub answers at, old and new, so the bar's
 * "Ticket Mgmt" can say it is the place you are standing in whichever of
 * them you arrived by. The old ones stay because the bell, chat, the
 * contractor list's own links and a year of bookmarks all use them.
 */
const HUB_PATHS = ['/manage', '/approvals', '/work-orders', '/search',
  '/templates', '/sla', '/admin-analytics', '/reports', '/invoicing', '/contractors', '/contractor-profile',
  // Documentation is a hub tab for an administrator (Gordon, 24 October),
  // and /docs/:id - where every link inside the docs goes - is that tab too.
  '/docs'];
// /tickets exactly: /tickets/:id is one ticket, which you may have opened
// from My Tickets, and that is the group that should light for it.
const onHub = (pathname) => pathname === '/tickets' || HUB_PATHS.some((p) =>
  pathname === p || pathname.startsWith(`${p}/`));

/*
 * The addresses that are the contractor portal on one of its tabs, so the
 * bar's Portal is lit on each of them. Exact matches only: /my-tickets/:id is
 * one ticket on its own page, and the Tickets group claims that.
 */
const PORTAL_PATHS = ['/contractor-dashboard', '/my-tickets', '/my-work-orders', '/assets', '/docs'];
// The register and its devices too: for a contractor they are the portal's
// Assets tab (only the contractor's bar draws Portal, so staff are unaffected).
// Documentation likewise: for a contractor it is the portal's Docs tab.
const onPortal = (pathname) => PORTAL_PATHS.includes(pathname) || pathname.startsWith('/assets/')
  || pathname.startsWith('/docs/');

/* /manage/docs (and /manage/docs/:id): the same doc at /docs, drawn in the
   right frame for whoever is asking (the routes below). */
function DocsAlias() {
  const { id } = useParams();
  return <Navigate to={id ? `/docs/${id}` : '/docs'} replace />;
}

/*
 * TO THE FRONT DOOR, REMEMBERING WHERE FROM. Every guard that used to be
 * <Navigate to="/login" /> or <Navigate to="/contractor-login" /> is this:
 * the address somebody was trying to open (path and query) is kept for the
 * moment the server says who they are (lib/returnPath; the effect in
 * AppShell takes them there), and the redirect is a `replace`, so Back does
 * not walk through four redirects to get out. One door: /contractor-login
 * has been an alias of /login since the two merged.
 */
function ToLogin() {
  const { pathname, search } = useLocation();
  rememberReturnPath(pathname + search);
  return <Navigate to="/login" replace />;
}

/* Reading the id off window.location by hand breaks on a trailing slash and on
   a query string, and silently - it just redirects to the wrong place. The
   router already knows the id. */
function ToContractorTicket() {
  const { id } = useParams();
  // The tab comes along: /tickets/:id/work is /my-tickets/:id/work.
  const rest = useLocation().pathname.replace(/\/+$/, '').split('/').slice(3).join('/');
  return <Navigate to={`/my-tickets/${id}${rest ? `/${rest}` : ''}`} replace />;
}

/*
 * THE TICKET PAGE'S TABS, as sub-routes (October 2026 redesign): one shell
 * (components/ticket/TicketLayout.js) and these under it, for staff at
 * /tickets/:id and for a contractor at /my-tickets/:id. Each is linkable, Back
 * works, and only the open one loads its data. "details" is the phone's
 * Details tab; the shell draws it. /tickets/:ticketId/session (the call) is a
 * route of its own, outside the shell, and no tab is named "session".
 */
const ticketTabs = [
  <Route key="c" index element={<ConversationTab />} />,
  <Route key="w" path="work" element={<WorkTab />} />,
  <Route key="d" path="device" element={<DeviceTab />} />,
  <Route key="o" path="docs" element={<DocsTab />} />,
  <Route key="f" path="files" element={<FilesTab />} />,
  <Route key="h" path="history" element={<HistoryTab />} />,
  <Route key="x" path="details" element={null} />
];

/*
 * THE DEVICE PAGE'S TABS, as sub-routes (October 2026 redesign): one shell
 * (components/asset/AssetLayout.js, which pages/AssetDetail.js is) and these
 * under it, at /assets/:id for staff and - drawn in the portal's Assets pane
 * - for a contractor. The old address is the Overview.
 */
const assetTabs = [
  <Route key="o" index element={<AssetOverview />} />,
  <Route key="h" path="history" element={<AssetHistory />} />,
  <Route key="t" path="tickets" element={<AssetTickets />} />,
  <Route key="d" path="device" element={<AssetDevice />} />,
  <Route key="n" path="notes" element={<AssetNotes />} />,
  <Route key="f" path="files" element={<AssetFiles />} />,
  <Route key="a" path="access" element={<AssetAccess />} />
];

const CHEVRON = (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6.5 8 10.5 12 6.5" />
  </svg>
);

/*
 * THE ADMINISTRATORS' OWN DOORS, marked as such. Gordon: "Admin top bar menu -
 * highlight slightly differently Ticket Mgmt, Asset Portal and Advanced to
 * show they are Admin menu choices."
 *
 * Slightly: the words keep the bar's own colour, weight and size (e2e/
 * mobile.mjs measures every item in the row for contrast), and what sets the
 * three apart is a thin rule before them and a small shield in the NCMB
 * accent beside each word - the accent's text colour, which is legible on
 * the bar in both themes and is not the blue that means "you are here". On a
 * phone, where there is no row to divide, they are a section of the sheet
 * captioned "Admin" instead. Said in words too, for a pointer (title) and a
 * screen reader (aria-describedby, onto one hidden note): "Administrators only".
 *
 * Only ever drawn inside the administrator's bar, so View as - which swaps
 * the whole bar for the viewed person's - shows none of it, as they would
 * see none of it.
 */
const ADMIN_ONLY = 'Administrators only';
const ADMIN_NOTE_ID = 'nav-admin-note';
// The words the three are described by, once, out of sight - a description
// needs no box of its own, and a hidden element still lends its text.
const ADMIN_NOTE = <span id={ADMIN_NOTE_ID} hidden>{ADMIN_ONLY}</span>;
const ADMIN_GLYPH = (
  <svg className="nav-admin-glyph" viewBox="0 0 16 16" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M8 1.75 3 3.6v3.9c0 3.05 2.1 5.6 5 6.75 2.9-1.15 5-3.7 5-6.75V3.6L8 1.75Z" />
    <path d="M8 1.75 3 3.6v3.9c0 3.05 2.1 5.6 5 6.75V1.75Z" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * A group of links in the top bar.
 *
 * Gordon: "in the admin view the menu items on the top bar need a redesign -
 * they are not visible/practible to use - perhaps a dropdown would be a
 * better option."
 *
 * Seven top-level items became five, three of them groups - but folding links
 * into a menu costs something, and it is the thing that would have made this
 * worse rather than better: an item inside a closed menu cannot show that it
 * is the page you are on. Stand on /approvals with Approvals tucked under
 * "Work" and nothing anywhere in the bar is highlighted, so the bar stops
 * answering "where am I" at exactly the moment it has fewer words to read.
 *
 * So the group button carries the active state of whichever child matches. The
 * match is on the path rather than on a click, because arriving by a link, a
 * redirect or a typed URL all have to look the same.
 */
/**
 * `expanded` is the phone case. Inside the burger sheet a group is not a
 * dropdown - there is nothing to drop down from - so its items are listed
 * under the group's name as a section, and there is no toggle to find.
 */
/**
 * `also` is a list of path prefixes that count as inside the group without
 * being items of it: /tickets/:id is one ticket, opened from My Tickets, and
 * the Tickets group should say so even though /tickets itself belongs to the
 * hub.
 */
/**
 * `admin` marks a group as one of the administrators' own (ADMIN_GLYPH above):
 * the shield beside its word and "Administrators only" said for it. Inside the
 * sheet the group sits in the Admin section already, so it needs neither.
 */
function NavMenu({ label, items, expanded = false, also = [], admin = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const here = useLocation();
  // Longest-prefix, so /my-tickets/:id lights "Tickets" and /tickets does
  // not also light a group that merely owns /tickets/create.
  const holdsHere = items.some((item) => here.pathname === item.to
    || (item.to !== '/' && here.pathname.startsWith(`${item.to}/`)))
    || also.some((p) => here.pathname.startsWith(p));

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (expanded) {
    return (
      <li className="nav-menu is-section" ref={ref}>
        <span className={`nav-section-label${holdsHere ? ' active' : ''}`}>{label}</span>
        <div className="nav-menu-panel is-inline" role="group" aria-label={label}>
          {items.map((item) => (
            <NavLink key={item.to} to={item.to}>{item.label}</NavLink>
          ))}
        </div>
      </li>
    );
  }

  return (
    <li className={`nav-menu${admin ? ' nav-admin' : ''}`} ref={ref}>
      <button
        type="button"
        className={`nav-menu-btn${holdsHere ? ' active' : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        aria-current={holdsHere ? 'true' : undefined}
        title={admin ? ADMIN_ONLY : undefined}
        aria-describedby={admin ? ADMIN_NOTE_ID : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {admin && ADMIN_GLYPH}
        {label}
        {CHEVRON}
      </button>
      {open && (
        <div className="nav-menu-panel" role="menu">
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} role="menuitem" onClick={() => setOpen(false)}>
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </li>
  );
}

/**
 * Everything below the router, and it has to be below it.
 *
 * Every route here is gated by a ternary - isAdmin(), isContractorLoggedIn()
 * and so on - and a ternary in an `element` prop is evaluated when *this*
 * component renders, not when the route is matched. While the router sat
 * inside this component, a navigation re-rendered the router and not this, so
 * the gates were whatever they had been on the last full page load.
 *
 * That was not academic. Signing in as a contractor stored the session and
 * went to /contractor-dashboard, whose element had been decided while there
 * was no session: Navigate to /contractor-login, which had likewise been
 * decided as Navigate to /login. The contractor typed a correct code and
 * arrived back at the login page, signed in, with no way through but a reload.
 *
 * With the router above it, a navigation re-renders this and every gate is
 * read fresh.
 */
/**
 * "Signing you in…" - shown while the app finds out who this is. It fades in
 * after a moment, so a quick answer never flickers it; a person this device
 * remembers is greeted by name.
 */
function SigningIn() {
  const memo = rememberedSignIn();
  const who = memo?.method === 'microsoft' ? firstName(memo.name) : null;
  return (
    <div className="signing-in" role="status" aria-live="polite" data-signing-in="">
      <span className="signing-in-ring" aria-hidden="true" />
      <p className="signing-in-text">{memo ? 'Signing you in…' : 'Opening Jarvis…'}</p>
      {who && <p className="signing-in-who">Welcome back, {who}</p>}
    </div>
  );
}

function AppShell() {
  const [backendConnected, setBackendConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  // Re-renders this component on every navigation, which is the whole point;
  // also the honest source for the path, since window.location is only right
  // by coincidence of having just re-rendered.
  const location = useLocation();

  /**
   * THE BURGER, on a phone only.
   *
   * The navbar's links were a horizontal strip that scrolled sideways at
   * phone width. Gordon: "The horizontal scroller top bar is not ideal here -
   * as i can hardly see what i am choosing. This needs changing to a burger
   * or something else" - and, asked, "for mobile viewing only."
   *
   * So below 1180px the links leave the bar and live in a sheet under it,
   * opened by one button; the group menus become sections in that sheet
   * rather than dropdowns, so every destination is a full-width row a thumb
   * can read before pressing. Above 1180px nothing changes: the bar is the
   * five-item bar it was.
   *
   * `narrow` is the same breakpoint as the stylesheet's, read here because a
   * group has to know whether to render as a dropdown or a section, and CSS
   * cannot change what React renders. The sheet closes on navigation, on
   * Escape, and on a press anywhere outside the bar.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 1180px)').matches : false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 1180px)');
    const onChange = (e) => { setNarrow(e.matches); if (!e.matches) setMenuOpen(false); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onPointer = (e) => {
      if (!e.target.closest || !e.target.closest('.navbar')) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [menuOpen]);

  /**
   * HOW TALL THE NAVBAR IS, told to the stylesheet.
   *
   * The navbar is sticky, and on a phone it wraps to two rows, so its height
   * is not a number anybody can write in CSS. Two things need it: the portal's
   * tab strip, which Gordon wants pinned just below the Jarvis logo, and the
   * group menus (Tickets, Work, Reports), which on a phone open as a sheet
   * directly under the bar. Measured here, once, and published as --navbar-h
   * on the root, rather than each of those guessing a pixel count that is
   * wrong the moment the bar wraps.
   */
  const navRef = useRef(null);
  useEffect(() => {
    const el = navRef.current;
    const root = document.documentElement;
    if (!el) { root.style.setProperty('--navbar-h', '0px'); return undefined; }
    const publish = () => root.style.setProperty(
      '--navbar-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', publish);
    };
    // Re-measured whenever the bar is mounted or unmounted - chromeless pages
    // have no bar - which is what `loading` and the path changing cover.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.pathname]);

  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/health`);
        setBackendConnected(response.status === 200);
      } catch (error) {
        setBackendConnected(false);
      }
    };

    checkBackend();
    const interval = setInterval(checkBackend, 30000);
    return () => clearInterval(interval);
  }, []);

  // The role now comes from the server. This used to read localStorage and
  // default to 'admin', so every visitor was an administrator as far as the
  // interface was concerned - and anyone could change it from the console.
  const me = useMe();
  useEffect(() => {
    if (me === null || me === undefined) return;
    // Switched off by an administrator: App swaps this whole shell for
    // AccessSwitchedOff. Nothing here - least of all the reload below - may
    // run on its way out.
    if (me.deactivated) return;
    // Signed out with an impersonation stored means the stored value is what is
    // wrong - a deleted user, a stale id, anything. Clearing it is always safe
    // and it is the difference between "sign in again" and being locked out of
    // your own app, which is what happened.
    // Not when /auth/me simply could not be reached (lib/me `failed`): that
    // is not the server's word on anything, and a reload here would only ask
    // the same unreachable server again.
    if (!me.signedIn && !me.failed && storedViewAs()) {
      clearViewAs();
      window.location.reload();
      return;
    }
    // Impersonation is only honoured for a confirmed administrator - but while
    // one is in force /auth/me answers as the person being viewed, who may not
    // be an admin. Keep it allowed in that case too, otherwise the control that
    // ends the impersonation disappears and there is no way back.
    setViewAsAllowed(me.isAdmin || Boolean(me.viewedBy));
    setLoading(false);
  }, [me]);

  /*
   * WHETHER TO OFFER "ASSETS".
   *
   * An administrator always has it - including while the register is
   * switched off, because that is exactly when IT is entering devices, and
   * before the migrations have run, when the page says which ones to run.
   * Anybody else sees it once the register is on AND they hold a grant: a
   * menu item that opens onto "you may not see anything here" is a broken
   * link, not a feature. The server checks every request regardless.
   */
  const assetMeta = useAssetMeta(Boolean(me?.signedIn));
  const offerAssets = Boolean(me?.isAdmin)
    || Boolean(assetMeta?.enabled && assetMeta?.access?.any);
  const offerMyEquipment = Boolean(assetMeta?.my_equipment);

  /*
   * THE FEATURE SWITCHES (078, Settings -> Features): Links, To do,
   * Meetings, call recording and the AI, each on or off for this person.
   * A feature that is off leaves every bar - the three role bars and the
   * phone's menu sheet, which are drawn from the same items - and its pages
   * answer "switched off" (FeatureRoute) to anybody who arrives by a link.
   * Until the server has answered, everything is on (lib/features.js says
   * why); the server refuses regardless.
   */
  const featureState = useFeatures();
  const has = (key) => (featureState ? featureState.features[key] !== false : true);

  /*
   * DOCUMENTATION (083). Offered to somebody with something to read in it -
   * since 085 that is IT staff (Edit) and anybody an administrator has given
   * something to, staff or contractor (a menu item onto an empty page is a
   * broken link) - and always to an administrator, who has something to set
   * up even before migrate:083. Asked once the switch is known to be on; the
   * server decides what is inside.
   *
   * WHERE (Gordon, 24 October: "Docs - visible only to Admins and IT
   * contractors and editors. Move to Ticket Admin sub menu and create a copy
   * in the contractor portal."): not in the top bar any more. An
   * administrator has it as the last tab of Ticket Mgmt; a contractor as a
   * tab of the portal; a member of staff who may read something under
   * Tickets. /docs and /docs/:id draw the right one of the three.
   */
  const docsMe = useDocsMe(Boolean(featureState) && has('docs')
    && (Boolean(me?.signedIn) || Boolean(contractorToken()) || Boolean(viewingContractorId())));
  const offerDocs = has('docs') && (docsMe?.can_read === true
    || (Boolean(me?.isAdmin) && !viewingContractorId() && docsMe?.ready === false && !docsMe?.failed));

  /*
   * HELP (123): plain-language answers for every member of staff - the Help
   * space, on Documentation's engine, behind its own switch ('help', since
   * 3 November; it followed Documentation's before). On the staff
   * and administrator bars (and so in the phone's sheet), last of the
   * everyday words and before the administrators' own; offered once
   * GET /help/me says this person may read it - every member of staff once
   * 123 is in, and an administrator before then, to be told what to run.
   */
  const helpMe = useHelpMe(Boolean(featureState) && has('help') && Boolean(me?.signedIn) && !viewingContractorId());
  const offerHelp = has('help') && helpMe?.can_read === true;

  /*
   * AND FOR A CONTRACTOR. GET /assets/access answers the one word the portal
   * needs - 'none', 'read' or 'write' - for a portal token, and for an
   * administrator looking through View as (the interceptor names the
   * contractor). Asked once per identity; the Assets tab on the portal shows
   * only when the register is on AND the contractor has been given
   * something, for the reason above: a tab onto "nothing here" is a broken
   * link. The server still checks every request.
   */
  const [contractorAssets, setContractorAssets] = useState(false);
  // contractorToken() directly: isContractorLoggedIn() is declared further
  // down this function and is not yet in scope here.
  const portalWho = contractorToken() ? 'token' : (viewingContractorId() || '');
  useEffect(() => {
    if (!portalWho) { setContractorAssets(false); return undefined; }
    let live = true;
    axios.get(`${process.env.REACT_APP_API_URL}/assets/access`, contractorAuth())
      .then((r) => { if (live) setContractorAssets(Boolean(r.data?.enabled) && r.data?.asset_access !== 'none'); })
      .catch(() => { if (live) setContractorAssets(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalWho, me]);

  /*
   * MY EQUIPMENT FOR A CONTRACTOR (Gordon: "My Equipment is also applicable
   * for contractors who have any assets assigned to them"). In their bar,
   * after Tickets - where staff and administrators have it - and only while
   * GET /assets/mine says something is assigned to them: a device is
   * assigned to a person's NCMB account, so that is a contractor whose record
   * is linked to one (lib/my-equipment.js). Their own token, or, for an
   * administrator looking through View as, the contractor named - which the
   * server honours for an administrator only. Nothing else in the portal
   * changes: its Assets tab is still the register a contractor has been
   * GRANTED a look at, which is a different question from what they hold.
   */
  const [contractorEquipment, setContractorEquipment] = useState(false);
  useEffect(() => {
    if (!portalWho) { setContractorEquipment(false); return undefined; }
    let live = true;
    axios.get(`${process.env.REACT_APP_API_URL}/assets/mine`, myEquipmentRequest())
      .then((r) => { if (live) setContractorEquipment(Boolean(r.data?.enabled) && (r.data?.assets || []).length > 0); })
      .catch(() => { if (live) setContractorEquipment(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalWho, me]);

  /*
   * BACK TO WHERE THEY WERE GOING. Somebody not yet signed in who opens an
   * emailed ticket link, a bell link, a meeting, a doc - or points a phone
   * at a device's label - is sent to sign in, and ToLogin (or TagLanding)
   * remembers where they were going (lib/returnPath). Once somebody is
   * signed in, by either door, this takes them there; otherwise signing in
   * dropped them on My tickets and the ticket, the meeting or the printer
   * they were standing at was forgotten. Only ever to a path of this app's
   * own, and only once: the path is taken as it is read.
   */
  const goTo = useNavigate();
  const contractorIn = Boolean(contractorToken());
  // `bound` holds the routes off the screen for the one render between
  // asking the router to go and it having gone: the router's navigation is
  // asynchronous, and in that render the page at the OLD address would
  // mount - and / is a <Navigate> of its own, which won the race and left
  // everybody on My tickets. Cleared by the first change of address.
  const [bound, setBound] = useState(false);
  const boundAt = useRef(null);
  useEffect(() => {
    if (!me?.signedIn && !contractorIn) return;
    const pending = takeReturnPath();
    if (pending && pending !== location.pathname + location.search) {
      boundAt.current = location.key;
      setBound(true);
      goTo(pending, { replace: true });
    }
  }, [me, contractorIn, location.pathname, location.search, location.key, goTo]);
  useEffect(() => {
    if (bound && boundAt.current !== location.key) setBound(false);
  }, [bound, location.key]);

  /**
   * MY OWN STATUS, loaded once somebody is identified.
   *
   * Held in lib/chatStatus's little store rather than in React state, because
   * three things that never meet read it: the account menu draws it, the chat
   * screen's chime asks whether to make a noise, and IncomingCall asks
   * whether to ring. Loading it here rather than in any of the three means
   * none of them has to be mounted for the other two to be right.
   *
   * Re-read every few minutes, because a status can CLEAR ITSELF - "for an
   * hour" is one of the choices - and nothing tells this browser when that
   * moment arrives. Cheap, and the alternative is somebody still showing as
   * Do not disturb at six because they set it at nine.
   */
  useEffect(() => {
    const signedIn = Boolean(me?.signedIn) || isContractorLoggedIn();
    if (!signedIn) { forgetMyStatus(); return undefined; }
    const read = () => { loadMyStatus().catch(() => {}); };
    read();
    const id = setInterval(read, 3 * 60 * 1000);
    return () => clearInterval(id);
  }, [me]);

  /**
   * A file dropped anywhere else must not throw the page away.
   *
   * This is not a nicety. A browser given a file it was not asked to handle
   * navigates the tab to that file - so dropping a screenshot an inch wide of
   * the attachment box on Create Ticket replaced the whole application with
   * the image, and everything typed into the form went with it. Gordon lost a
   * description that way, and from the outside it looked like two unrelated
   * faults: the attachment did not attach, and the text did not save.
   *
   * So the window refuses drops by default, and the components that actually
   * want one call stopPropagation in their own handler before this sees it.
   * Refusing to do anything is the right default here: nothing on this page
   * has ever been openable by dropping a file on it, and the cost of being
   * wrong is somebody's unsaved work.
   */
  useEffect(() => {
    const swallow = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  // A contractor holds a portal token. Through lib/contractorAuth's wrapped
  // read: a plain localStorage.getItem throws in a private window (see
  // ContractorAvailabilityPage above). A token left behind by a contractor
  // on a machine a member of staff then signs in on is forgotten by lib/me
  // the moment /auth/me names the member of staff - staff identity wins -
  // so this never hijacks a shared route for them.
  const isContractorLoggedIn = () => Boolean(contractorToken());

  // Both answer from the server's view, not the browser's.
  const isAdmin = () => Boolean(me?.isAdmin);
  const isStaff = () => Boolean(me?.signedIn) && !me?.isAdmin;
  // An approver is whoever the SERVER says may sign things off. Today that is
  // every administrator and nobody else - staff-auth grants one for the other -
  // but the hours queue routes a request to a named approver by user id, and
  // mayDecide lets that person decide whether or not they administer anything.
  // Gating the page on isAdmin alone meant a named approver who was not an
  // administrator would be redirected away from a queue the API would have let
  // them act on. No one is in that position yet; this stops it becoming a
  // puzzle on the day somebody is.
  const isApprover = () => Boolean(me?.isApprover) || Boolean(me?.isAdmin);

  // Only the portal itself is chromeless. This used to test for the
  // '/contractor-' prefix, which also matched /contractor-profile/:id and
  // stripped the navbar off an admin page.
  const isContractorPortal = () => {
    const path = location.pathname;
    return path.startsWith('/contractor-login') || path.startsWith('/contractor-dashboard')
      || path.startsWith('/login');
  };

  /*
   * ONE HEADER FOR A CONTRACTOR, EVERYWHERE THEY GO.
   *
   * Gordon: "i note that when i click on chat in the contractor view i have a
   * different header and menu. Can we standardise here?"
   *
   * He was looking at two headers built by two different pieces of code. The
   * portal is chromeless - isContractorPortal() hides this navbar - so
   * ContractorDashboard grew a header of its own carrying the logo, the chat
   * link, the bell, the theme switch and Logout. Walk to /chat, which is not a
   * portal path, and the real navbar appears instead: different layout,
   * different controls, and no way back to the portal that looks like the way
   * they just came.
   *
   * So a signed-in contractor keeps the navbar on the portal too. It already
   * carries the three links that are theirs - Portal, My work orders, Chat -
   * and the bell and account menu sit in it the same way round as everywhere
   * else. The dashboard's duplicate row is gone; what is left there is the
   * welcome line, which is page content rather than chrome.
   *
   * The front door and the code-entry page stay chromeless: there is nobody
   * signed in yet to put in a navbar.
   */
  /*
   * THE HEADER WENT MISSING FOR THE ONE PERSON MOST LIKELY TO NEED IT.
   *
   * Gordon: "On the mobile version in the contractor view pls check as the top
   * header with portal and chat is missing (when viewing as a contractor)."
   *
   * It was not a mobile problem at all - it was gone at every width. This read
   * `isContractorPortal() && !isContractorLoggedIn()`, and
   * isContractorLoggedIn() is a contractor_token in localStorage. An
   * administrator using "View as" has no such token: they are themselves, with
   * a header telling the server whose eyes to use. So the portal went
   * chromeless for them - no navbar, no Portal link, no Chat, no bell, and no
   * way out except the browser's back button.
   *
   * Which is exactly backwards. A real contractor signs in and finds their
   * way around; an administrator previewing the portal is the one who most
   * needs a way back to the rest of the application.
   *
   * Chromeless now means what it was always trying to mean: nobody is signed
   * in yet. The front door and the code-entry page qualify. Being on the
   * portal does not, whether you got there with a token or with View as.
   */
  /*
   * Somebody looking at the contractor portal, by either route: a contractor
   * holding a token, or an administrator using "View as". One helper rather
   * than the test written out twice, because two copies are how the navbar
   * came back without its links in it.
   *
   * viewingContractorId() rather than isViewingAs('contractor'): the
   * gated read answers null until /auth/me has confirmed the caller is an
   * administrator, so on the first render of a page load the navbar would
   * draw without these links and then grow them a moment later. The stored
   * value only decides what to DRAW here; the server still refuses the data
   * to anyone who is not an administrator, so reading it early grants
   * nothing. And it is specifically the contractor kind - an administrator
   * viewing a member of STAFF has no business being offered a portal.
   */
  const viewingPortal = () =>
    isContractorLoggedIn() || Boolean(viewingContractorId());

  const chromeless = () => {
    const path = location.pathname;
    if (path.startsWith('/login') || path.startsWith('/contractor-login')) return true;
    // An outside guest's way into a meeting (/join/:token): nobody is signed
    // in, and a navbar of pages they cannot open is not a header. The page
    // draws its own small one.
    if (path.startsWith('/join/')) return true;
    return isContractorPortal() && !isContractorLoggedIn()
      && !me?.signedIn && !viewingContractorId();
  };

  // The front door is the one page that offers Microsoft itself, in a door of
  // its own. The floating SignIn put a second, differently styled "Sign in
  // with Microsoft" in the corner above it - two buttons doing one thing, and
  // the smaller one landing on top of the page that exists to make that
  // choice. The theme toggle is still welcome there.
  const isFrontDoor = () => location.pathname.startsWith('/login');

  // The dashboard has a header of its own with a Logout button in it, so the
  // theme toggle belongs beside that rather than floating over the page. The
  // login page has no header, so there it keeps the corner.
  const isContractorDashboard = () =>
    location.pathname.startsWith('/contractor-dashboard');

  /*
   * Ticket Mgmt, Asset Portal and Advanced: the administrator's bar ends with
   * them, and they are marked as theirs (see ADMIN_GLYPH). In the bar each is
   * its own item, the first carrying the rule that sets the three apart; in
   * the phone's sheet (`inSheet`) they are one section, "Admin", whose rows
   * are the two links and the Advanced group under its own small heading.
   */
  const adminDoors = (inSheet) => {
    const hub = onHub(location.pathname);
    const mark = inSheet ? {} : { title: ADMIN_ONLY, 'aria-describedby': ADMIN_NOTE_ID };
    const glyph = inSheet ? null : ADMIN_GLYPH;
    const doors = [
      /* Lit on every address the hub answers at, old or new (HUB_PATHS):
         standing on /approvals from a link in the bell is standing in
         Ticket Mgmt. */
      <li key="hub" className={inSheet ? undefined : 'nav-admin nav-admin-start'}>
        <NavLink to="/manage" className={hub ? 'active' : undefined}
                 aria-current={hub ? 'page' : undefined} {...mark}>
          {glyph}Ticket management
        </NavLink>
        {!inSheet && ADMIN_NOTE}
      </li>,
      <li key="assets" className={inSheet ? undefined : 'nav-admin'}>
        <NavLink to="/assets" {...mark}>{glyph}Asset Portal</NavLink>
      </li>,
      <NavMenu
        key="advanced"
        expanded={inSheet}
        admin={!inSheet}
        label="Advanced"
        items={[
          { to: '/audit-log', label: 'Audit log' },
          // Beside the audit log because it is the same kind of page: a
          // record to consult when something has to be looked into. Shown
          // whether or not the firm has switched it on - switched off, the
          // page says what it is and where it is turned on, and "Who
          // looked" stays readable either way.
          { to: '/chat-archive', label: 'Chat archive' },
          // Every recorded call, whoever was on it; the same page shows
          // everybody else their own, from the account menu. Not while
          // recording is switched off for this administrator (078).
          ...(has('recording') ? [{ to: '/recordings?scope=all', label: 'Call recordings' }] : []),
          // 115: who might be in the building, for a fire or a drill.
          { to: '/roll-call', label: 'Emergency roll-call' },
          // Joiners & leavers (113): the checklists, who has not signed in,
          // and approvers away - worked through rather than consulted, so
          // after the records and before Settings.
          { to: '/people', label: 'Joiners & leavers' },
          // 125: announcements are written and followed up - work, like
          // the checklists - so here rather than in Settings.
          { to: '/announcements', label: 'Announcements' },
          { to: '/settings', label: 'Settings' }
        ]}
      />
    ];
    if (!inSheet) return doors;
    return (
      <li className="nav-menu is-section nav-admin-section">
        <span className="nav-section-label is-admin" id="nav-admin-caption">
          {ADMIN_GLYPH}Admin
        </span>
        {ADMIN_NOTE}
        <ul className="nav-admin-list" role="group" aria-labelledby="nav-admin-caption"
            aria-describedby={ADMIN_NOTE_ID}>
          {doors}
        </ul>
      </li>
    );
  };

  return (
    <div className="app">
        {/* Only the chromeless pages need a floating corner now: the front
            door and the code-entry page, where nobody is signed in yet and
            there is no navbar to put anything in. A signed-in contractor has
            the navbar on the portal like everywhere else. */}
        {/* Not on a guest's page (/join/:token), which draws its own small
            header with the theme switch in it. */}
        {!loading && chromeless() && !location.pathname.startsWith('/join/') && (
          <div className="floating-role-switcher">
            {/* No header here for SignIn to say it in, so the "could not
                reach the server" line lives in this corner too. */}
            <ServerReachNote />
            {/* Moved into the dashboard's own header, next to Logout. */}
            {!isContractorDashboard() && <ThemeToggle />}
            {/* No Microsoft sign-in anywhere in the contractor portal. A
                contractor has no Microsoft account here - they sign in with a
                code to their own address - so the button could only ever fail
                for the person it was shown to. An administrator previewing the
                portal gets back out through the picker below. */}
            {/* No header here to put the picker in, so this corner carries both
                halves - and the banner is what tells an administrator whose
                portal they are looking at. */}
            {!isFrontDoor() && (isAdmin() || me?.viewedBy || storedViewAs())
              && <ViewAsBar isAdmin={isAdmin()} />}
          </div>
        )}

        {/* A RINGING CALL REACHES SOMEBODY WHEREVER THEY ARE, which is why
            this is in the shell and not on a page. It is deliberately NOT
            inside the navbar block below: that block is hidden on every
            contractor portal page, and the portal is exactly where a
            contractor is sitting when somebody rings them. */}
        {/* WHOSE calls to ring about, stated rather than guessed. This was
            left to contractorAuth(), which hands back a contractor token
            whenever one is in localStorage - and nothing clears that token
            when staff sign in, so on any browser where a contractor had ever
            signed in, an administrator's own tab polled as the contractor and
            was never told about its own calls. Staff identity wins when there
            is one. The long version is in IncomingCall. */}
        {!loading && (isAdmin() || isStaff() || isContractorLoggedIn()) && (
          <IncomingCall asContractor={!me?.signedIn && isContractorLoggedIn()} />
        )}

        {/* TO-DO REMINDERS, for the same reason and in the same place: a
            nudge that only came on the to-do page would only reach people
            already looking at their to-dos. Whose, stated as for the calls:
            a contractor's own token; staff through the interceptor; and an
            administrator looking through a contractor's portal names them
            (as=c:<id>), exactly as the to-do page does - so a card's Open
            lands on the list it came from. Keyed on who, so a change of
            identity starts a fresh poller rather than one that had stopped. */}
        {/* Not with To do switched off for this person (078): the poll
            would only be refused, and there is nothing to remind them of.
            Started once the answer is in rather than on the optimistic
            "on" before it, so a switched-off list is never polled at all. */}
        {!loading && featureState && has('todos') && (isAdmin() || isStaff() || isContractorLoggedIn()) && (() => {
          const asContractor = !me?.signedIn && isContractorLoggedIn();
          const asParam = me?.signedIn && viewingContractorId()
            ? `c:${viewingContractorId()}` : null;
          return (
            <ReminderAlerts key={`${asContractor ? 'c' : 's'}-${asParam || ''}`}
                            token={asContractor ? contractorToken() : undefined}
                            asParam={asParam}
                            todoPath={viewingPortal() ? '/todo' : '/todos'} />
          );
        })()}

        {/* The navbar, on every page with somebody signed in - including the
            contractor portal, which used to hide it and build its own header
            instead. That was the "different header and menu" Gordon walked
            into going from the portal to /chat. */}
        {!chromeless() && !loading && (
          <nav className="navbar" ref={navRef}>
            <div className="navbar-content">
              {/* Home for an administrator is the hub's Dashboard, which
                  is where / sends them too. */}
              <Link to={isAdmin() ? "/manage" : "/my-tickets"} className="logo" aria-label="Jarvis home">
                <Logo />
              </Link>

              {/* Phone only (App.css hides it above 1180px). */}
              <button
                type="button"
                className="nav-burger"
                aria-label={menuOpen ? 'Close the menu' : 'Open the menu'}
                aria-expanded={menuOpen}
                aria-controls="nav-links"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <span className="nav-burger-bars" aria-hidden="true"><i /><i /><i /></span>
                <span className="nav-burger-word">Menu</span>
              </button>
              {/* Chat beside Menu on a narrow screen, and so not in the menu
                  sheet as well (see ChatIconLink). */}
              {narrow && <ChatIconLink />}
              {/* Scan a label or a barcode (Lookup): one tap from any screen
                  on a phone, for whoever may look at the register. */}
              {narrow && offerAssets && (
                <NavLink to="/assets/lookup" className="nav-scan-icon"
                  aria-label="Scan a device" title="Scan a device">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
                    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3M4 12h16" />
                  </svg>
                </NavLink>
              )}

              <ul id="nav-links" className={`nav-links${menuOpen ? ' is-open' : ''}`}>
                {/* THE SEARCH (3 November), first in the phone's sheet: the
                    bar has no room for it at that width. Above 1180px it is
                    the box in navbar-right instead.
                    A search icon in the bar itself was tried and measured on
                    3 November: beside Chat and Scan it takes the whole logo
                    away on an administrator's 375px bar (14px of it left at
                    390px) and shrinks it for staff who scan - so no icon. */}
                {narrow && (
                  <li className="nav-search-row">
                    <SearchButton inSheet />
                  </li>
                )}
                {/* Admin/Approver Navigation */}
                {/*
                    FIVE ITEMS, NOT SEVEN, and the three that are groups are
                    grouped by what somebody is trying to do rather than by
                    what was left over.
                    //
                    Approvals, Work orders and Contractors used to sit in the
                    bar as three separate words and are one job - deciding and
                    handing out work - so they are one menu. "More" was a
                    bucket; it is now "Reports", which says what is in it, with
                    Settings beside it as its own item because it is where the
                    firm's rules and the automation builder live and it is
                    reached often.
                    //
                    Chat stays a top-level item: it is the only one that
                    carries a number, and a count hidden inside a closed menu
                    is a count nobody sees. */}
                {/*
                    AND NOT WHILE LOOKING AT A CONTRACTOR'S PORTAL.
                    //
                    Gordon: "when i am logged in as admin and i click on view
                    as and i chose a contractor i do not have all the
                    contractor options in the header menu - at the same time i
                    have all the admin options there."
                    //
                    Viewing as a member of STAFF already behaves, because the
                    server swaps req.user and isAdmin() comes back false - the
                    whole app follows one answer. A contractor is not a users
                    row, so there is nothing for that header to name and the
                    server keeps answering "administrator": every item below
                    kept rendering, over a portal, which is both wrong about
                    who you are pretending to be and twice the navbar anybody
                    needs. The point of View as is to see what they see.
                    //
                    viewingContractorId() rather than viewingPortal(),
                    which is the looser "is a portal on screen" and is also
                    true of a signed-in administrator with a stale
                    contractor_token left in this browser - see the note in
                    IncomingCall.js, where exactly that made an admin's
                    browser ring as somebody else. Losing the whole admin
                    navbar to a leftover token would be a worse bug than the
                    one being fixed. This hides it only when an administrator
                    has deliberately chosen to look as a contractor. */}
                {/*
                    THE THREE BARS, REDRAWN. Gordon, 21 October, gave each
                    role its bar in order, and the orders below are his:
                    //
                      staff        Chat · To do · Tickets ▾ · My Equipment
                      contractor   Portal · Chat · To do · Tickets ▾ · Availability
                      admin        Chat · To do · Tickets ▾ · My Equipment ·
                                   Ticket Mgmt · Asset Portal · Advanced ▾
                    //
                    The shape is the same for all three: the things everybody
                    does first - talk, the day's list, their own tickets -
                    and then what the role adds. For an administrator what it
                    adds is three doors rather than a row of pages: Ticket
                    Mgmt is a hub (pages/ManageHub.js) with its own strip
                    carrying everything that used to be Dashboard, Work and
                    Reports; Asset Portal is the register; Advanced holds the
                    records and the firm's settings.
                    //
                    "To do" rather than "To Do": Gordon wrote both, and the
                    rest of the bar, the page title and a dozen tests all say
                    "To do", so that is the one spelling everywhere. */}
                {/*
                    AND NOT WHILE LOOKING AT A CONTRACTOR'S PORTAL.
                    //
                    Gordon: "when i am logged in as admin and i click on view
                    as and i chose a contractor i do not have all the
                    contractor options in the header menu - at the same time i
                    have all the admin options there."
                    //
                    Viewing as a member of STAFF already behaves, because the
                    server swaps req.user and isAdmin() comes back false - the
                    whole app follows one answer. A contractor is not a users
                    row, so there is nothing for that header to name and the
                    server keeps answering "administrator": every item below
                    kept rendering, over a portal, which is both wrong about
                    who you are pretending to be and twice the navbar anybody
                    needs. The point of View as is to see what they see.
                    //
                    viewingContractorId() rather than viewingPortal(),
                    which is the looser "is a portal on screen" and is also
                    true of a signed-in administrator with a stale
                    contractor_token left in this browser - see the note in
                    IncomingCall.js, where exactly that made an admin's
                    browser ring as somebody else. Losing the whole admin
                    navbar to a leftover token would be a worse bug than the
                    one being fixed. This hides it only when an administrator
                    has deliberately chosen to look as a contractor. */}
                {isAdmin() && !viewingContractorId() && (
                  <>
                    {/* Chat stays a top-level item: it is the only one that
                        carries a number, and a count hidden inside a closed
                        menu is a count nobody sees. On a phone it is the
                        icon beside Menu instead. */}
                    {!narrow && <ChatNavItem />}
                    {/* Meetings beside Chat (Gordon, 22 October): both are
                        talking to people, and a meeting is reached from
                        anywhere, like a conversation. Staff and
                        administrators organise them; a contractor is
                        invited to one from the bell or the invitation. */}
                    {/* Each of the next three only while its feature is on
                        for this person (078, Settings -> Features) - for an
                        administrator too: Off means off for everybody, and
                        Settings -> Features is where it comes back. */}
                    {has('meetings') && <li><NavLink to="/meetings">Meetings</NavLink></li>}
                    {has('todos') && <li><NavLink to="/todos">To do</NavLink></li>}
                    {has('links') && <li><NavLink to="/links">Links</NavLink></li>}
                    {/* Documentation is a tab of Ticket Mgmt for an
                        administrator (ManageHub), not a word up here. */}
                    <NavMenu
                      expanded={narrow}
                      label="Tickets"
                      also={['/tickets/']}
                      items={[
                        { to: '/my-tickets', label: 'My tickets' },
                        { to: '/create', label: 'Create ticket' },
                        // The service catalogue (124), while its switch is on.
                        ...(has('catalogue') ? [{ to: '/request', label: 'Request something' }, { to: '/my-requests', label: 'My requests' }] : [])
                      ]}
                    />
                    {/* Only once the firm has switched it on: a menu item
                        that opens onto "nothing here" is a broken link, not
                        a feature. The server says (lib/assets, my_equipment). */}
                    {offerMyEquipment && <li><NavLink to="/my-equipment">My equipment</NavLink></li>}
                    {offerHelp && <li><NavLink to="/help">Help</NavLink></li>}
                    {/* The administrators' own three (ADMIN_GLYPH): after a
                        thin rule and each with the shield in the bar; one
                        captioned section of the sheet on a phone. */}
                    {adminDoors(narrow)}
                  </>
                )}

                {/* Staff navigation.
                    Two items, because two is what a member of staff can
                    actually use. "My work orders" and "Contractors" were here
                    and neither could ever load: work orders belong to the
                    contractor who holds the job - /my-work-orders is resolved
                    from a portal token, which staff do not have - and the
                    contractor list is administrative. They answered 403 every
                    time, which read as the app being broken rather than as the
                    pages not being theirs. */}
                {isStaff() && (
                  <>
                    {!narrow && <ChatNavItem />}
                    {has('meetings') && <li><NavLink to="/meetings">Meetings</NavLink></li>}
                    {has('todos') && <li><NavLink to="/todos">To do</NavLink></li>}
                    {has('links') && <li><NavLink to="/links">Links</NavLink></li>}
                    {/* Documentation under Tickets, for the member of staff
                        who may read some - IT staff, who edit it and fix
                        tickets with it, and anybody given something. Staff
                        have no Ticket Mgmt, and the docs are about tickets:
                        runbooks, how-tos, fixes. Nobody else sees it. */}
                    <NavMenu
                      expanded={narrow}
                      label="Tickets"
                      also={['/tickets/']}
                      items={[
                        { to: '/my-tickets', label: 'My tickets' },
                        { to: '/create', label: 'Create ticket' },
                        ...(has('catalogue') ? [{ to: '/request', label: 'Request something' }, { to: '/my-requests', label: 'My requests' }] : []),
                        ...(offerDocs ? [{ to: '/docs', label: 'Docs' }] : [])
                      ]}
                    />
                    {offerMyEquipment && <li><NavLink to="/my-equipment">My equipment</NavLink></li>}
                    {/* Not on Gordon's list for staff, and not shown to most
                        of them: only a member of staff the firm has GRANTED
                        a view of the register sees this, once the register
                        is on. Without it the one person with a grant would
                        have no way to the page they were granted. */}
                    {offerAssets && <li><NavLink to="/assets">Assets</NavLink></li>}
                    {/* Help (123): every member of staff's, last. */}
                    {offerHelp && <li><NavLink to="/help">Help</NavLink></li>}
                  </>
                )}

                {/* CONTRACTOR NAVIGATION, which did not exist.
                    //
                    // The whole navbar is hidden on the portal pages, and this
                    // block only ever rendered for staff and administrators -
                    // so a contractor who navigated to /chat got the page with
                    // an empty navbar above it, and from their own dashboard
                    // there was no way to reach it at all. Gordon's words: "Did
                    // you set the chat functionality in the contractor view pls
                    // as i cannot see it."
                    //
                    // Three items, all of which a contractor can actually use.
                    // The two that were removed from the staff list for
                    // answering 403 are theirs: /my-work-orders is resolved
                    // from a portal token, which is exactly what they have. */}
                {/*
                    `viewingPortal` rather than isContractorLoggedIn(), for the
                    same reason chromeless() changed above: an administrator
                    using "View as" has no contractor token, so these three
                    links were hidden from them even once the navbar came back.
                    A navbar with no links in it is not a header, it is a rule
                    with a logo on it.
                    //
                    My work orders is resolved from a portal token, so it is
                    offered only to somebody who actually holds one - an
                    administrator gets the admin list at /work-orders from the
                    menu above instead, which shows every contractor's. */}
                {/*
                    ALL FOUR, FOR BOTH KINDS OF VIEWER. Three of these were
                    gated on isContractorLoggedIn() - a portal token - so an
                    administrator using View as got Portal and Chat and
                    nothing else, which is not what a contractor sees and was
                    the other half of Gordon's complaint.
                    //
                    They were gated that way because each page resolved its
                    contractor from that token alone and could not work
                    without one. That was the thing to fix rather than the
                    thing to route around: every endpoint behind them -
                    availability, to-dos, assignments, /my-work-orders - already
                    admits an administrator who names the contractor, so the
                    id was all that was missing. lib/contractorAuth's
                    portalContractor() supplies it, once, for all of them. */}
                {viewingPortal() && (
                  <>
                    {/* Portal opens the BOARD, always (Gordon, 22 October:
                        "when i click on contractor portal it should show me
                        the board") - /contractor-dashboard with no tab is
                        the board, and ContractorDashboard resets its strip
                        when the route loses its tab. It is lit on every
                        address that is the portal on a tab, because standing
                        on My Tickets or My Work Orders IS standing in the
                        portal; one ticket (/my-tickets/:id) is not. */}
                    <li>
                      <NavLink to="/contractor-dashboard"
                               className={onPortal(location.pathname) ? 'active' : undefined}
                               aria-current={onPortal(location.pathname) ? 'page' : undefined}>
                        Portal
                      </NavLink>
                    </li>
                    {/* "My work orders" was the second word here. It is a
                        tab of the portal now - Gordon's strip order puts it
                        beside My Tickets - and /my-work-orders opens the
                        portal on that tab, so nothing that linked to it
                        breaks. */}
                    {!narrow && <ChatNavItem />}
                    {/* Gordon: "Move availability to the top bar next to chat."
                        //
                        // It was the last tab on the portal, and it was the one
                        // tab that is not about a job in flight - the other five
                        // are all views of work the contractor is holding right
                        // now, and this is the standing answer to "when can you
                        // work". Sitting at the end of that row, it read as a
                        // sixth pile of work.
                        //
                        // Beside Chat is right for the same reason Chat is
                        // there: both are things a contractor does ABOUT the
                        // work rather than things in it, and both are reachable
                        // from wherever they happen to be rather than only from
                        // the portal. */}
                    {/* Gordon: "Move To Do on the top bar in between Chat and
                        availability." Same reasoning as Availability: about
                        the contractor's day, not a job in flight. */}
                    {has('todos') && <li><NavLink to="/todo">To do</NavLink></li>}
                    {/* Links (Gordon, 22 October): the firm's shelf of websites
                        and apps, for everybody. A contractor reads it; only staff
                        change it (routes/links.js). Both only while the
                        feature is on for them (078). */}
                    {has('links') && <li><NavLink to="/links">Links</NavLink></li>}
                    {/* Documentation (083) is a tab of the portal now - the
                        docs NCMB IT has shared, once there are some. */}
                    {/* Gordon, 21 October: Create Ticket first, the list
                        second - the other way round from the staff menu,
                        and kept that way because it is how he wrote it.
                        The list was My Tickets, the portal on its tickets
                        tab - every job assigned to them. Gordon, 22 October:
                        "when i click on tickets on the top bar and then on
                        my tickets - it should show the tickets that the
                        contractor opened and not all the assigned tickets."
                        So it is My Requests now: a page of only the tickets
                        they raised (ContractorRequests). The portal's own
                        My Tickets tab still shows the whole picture, jobs
                        and requests both; only this entry changed. A
                        request opens at /my-tickets/:id, so that lights the
                        group too. */}
                    <NavMenu
                      expanded={narrow}
                      label="Tickets"
                      also={['/my-tickets/', '/request/']}
                      items={[
                        { to: '/create', label: 'Create ticket' },
                        // Only the items marked for contractors too (124).
                        ...(has('catalogue') ? [{ to: '/request', label: 'Request something' }] : []),
                        { to: '/my-requests', label: 'My requests' }
                      ]}
                    />
                    {/* After Tickets, as in the staff and admin bars - only
                        for a contractor with a device assigned to them. */}
                    {contractorEquipment && <li><NavLink to="/my-equipment">My equipment</NavLink></li>}
                    <li><NavLink to="/availability">Availability</NavLink></li>
                  </>
                )}
              </ul>

              <div className="navbar-right">
                {/* The theme switch, "View as…" and Sign out all moved into
                    the account menu that SignIn renders: they are settings for
                    the account, and this corner was carrying three unlabelled
                    controls side by side. The loud "Viewing as" banner stays
                    exactly where it was - see below. */}
                {/* The contractor's own token, because the bell's requests
                    do not go through the Entra interceptor. The portal used
                    to pass this from its own header; now that the navbar is
                    the contractor's header too, it passes it here. */}
                {/* The global search's box (Ctrl+K opens it from anywhere);
                    on a phone it is the first row of the menu sheet. */}
                {!narrow && <SearchButton />}
                <NotificationsBell token={isContractorLoggedIn() && !me?.signedIn
                  ? contractorToken() : undefined}
                  todoPath={viewingPortal() ? '/todo' : '/todos'} />
                <SignIn />
                {backendConnected ? (
                  <span
                    className="conn-dot"
                    role="img"
                    aria-label="Connected to server"
                    title="Connected to server"
                  />
                ) : (
                  <span className="conn-badge">
                    <span className="status-dot"></span>
                    Disconnected
                  </span>
                )}
                {/* The firm, at the far end from the product.
                    The supplied lockup puts NCMB and Jarvis either side of a
                    rule, and at header height the whole thing renders its
                    tagline at about three pixels. Split across the two ends of
                    the bar, both are legible and each says its own thing: the
                    application on the left, where the logo is also the way
                    home, and whose application it is on the right. */}
                <a
                  className="navbar-firm"
                  href="https://www.ncmb.eu"
                  target="_blank"
                  /* noopener because the opened page gets a handle on this one
                     through window.opener otherwise, and noreferrer so the
                     ticketing system's URL is not sent along with it. */
                  rel="noopener noreferrer"
                  title="NCMB &mdash; opens www.ncmb.eu in a new tab"
                >
                  <LogoMark height={22} />
                </a>
              </div>
            </div>
          </nav>
        )}

        {/* THE GLOBAL SEARCH's palette (Ctrl+K, or the box in the bar), for
            anybody signed in - a contractor on their token, and an
            administrator looking through a contractor's portal searching as
            that contractor (lib/search.js). Recent searches are kept per
            person: under whoever the box searches as, and for View as under
            the administrator AND the person viewed, so neither sees the
            other's. Not on the chromeless pages, where nobody is signed in. */}
        {!chromeless() && !loading && (me?.signedIn || isContractorLoggedIn()) && (() => {
          const by = me?.viewedBy ? String(me.viewedBy.id || me.viewedBy.email || me.viewedBy) : null;
          const who = me?.signedIn
            ? (viewingContractorId() ? `u:${me.id}>c:${viewingContractorId()}`
              : by ? `${by}>u:${me.id}` : `u:${me.id}`)
            : `c:${contractorIdentity()?.id || 'contractor'}`;
          return <GlobalSearch key={who} who={who} isAdmin={isAdmin() && !viewingContractorId()} />;
        })()}

        {/* Says so when the API answered with less than it should. */}
        <DegradedBanner />

        {/* IMPORTANT ANNOUNCEMENTS (125): a strip across the top of every
            page, under the navbar, until the person presses "I've read
            this". The contractor's own token as the bell has it; viewing
            as somebody, theirs - read-only. Keyed on who, so a switch of
            View as asks again. */}
        {!chromeless() && !loading && (isAdmin() || isStaff() || isContractorLoggedIn()) && (
          <AnnouncementBanner
            key={`${me?.id || 'c'}-${viewingContractorId() || ''}`}
            token={isContractorLoggedIn() && !me?.signedIn ? contractorToken() : undefined} />
        )}

        {/* A change refused for want of a location, explained once rather than
            in each of the dozen screens that can make one. Beside
            DegradedBanner because it is the same kind of thing: a response
            condition that can come from anywhere and has to be surfaced
            somewhere. */}
        <LocationGate />

        {/* Fixed to the bottom-left corner, outside the header. */}
        {/* The banner only, and only in the places it already appeared. The
            picker that used to live here has moved into the header; on the
            contractor portal, which has no header, the floating switcher above
            carries both. */}
        {!loading && !chromeless() && (isAdmin() || me?.viewedBy || storedViewAs())
          && <ViewAsBar isAdmin={isAdmin()} mode="banner" />}

        {/* While Jarvis works out who this is - and, for somebody this device
            remembers, signs them quietly back in with Microsoft (lib/auth) -
            a calm "Signing you in…" rather than a blank page, and never a
            flash of the front door before it. Not on a guest's meeting link,
            which has nobody to sign in. */}
        {loading && !location.pathname.startsWith('/join/') && <SigningIn />}

        {!loading && !bound && (
          <Routes>
            {/* Admin Routes - Protected */}
            {/* An administrator's home is the Ticket Mgmt hub, on its
                Dashboard tab. The dashboard was the page at / and the first
                word in the bar; Gordon's bar has no Dashboard word, the hub
                has the tab, so / goes there. */}
            <Route
              path="/"
              element={isAdmin() ? <Navigate to="/manage" replace /> : <Navigate to="/my-tickets" replace />}
            />
            {/*
                THE TICKET MGMT HUB, at /manage and at every address its tabs
                used to be pages at. Each old path mounts the same hub with
                the matching tab lit rather than redirecting: the URL a
                bookmark, the bell or a test typed is the URL that stays in
                the bar, and the page is the same component it always was.
                The new paths are what the hub's own strip links to.
                //
                Approvals admits a named approver who is not an administrator
                (isApprover); the hub then shows them that one tab. */}
            {[
              ['/manage', 'dashboard'],
              ['/manage/approvals', 'approvals'], ['/approvals', 'approvals'],
              ['/manage/work-orders', 'work-orders'], ['/work-orders', 'work-orders'],
              ['/manage/tickets', 'tickets'], ['/tickets', 'tickets'],
              ['/manage/search', 'search'], ['/search', 'search'],
              ['/manage/templates', 'templates'], ['/templates', 'templates'],
              ['/manage/sla', 'sla'], ['/sla', 'sla'],
              ['/manage/analytics', 'analytics'], ['/admin-analytics', 'analytics'],
              ['/manage/reports', 'reports'], ['/reports', 'reports'],
              ['/manage/invoicing', 'invoicing'], ['/invoicing', 'invoicing'],
              ['/manage/contractors', 'contractors'], ['/contractors', 'contractors'],
              // The contractor contracts register (120): a tab of
              // Contractors, behind the Contracts switch.
              ['/manage/contracts', 'contracts'], ['/manage/contracts/:id', 'contracts'],
              // The service catalogue (124): Tickets -> Catalogue, behind its switch.
              ['/manage/catalogue', 'catalogue'], ['/manage/catalogue/:itemId', 'catalogue'],
              // Scheduled maintenance plans (128): Tickets -> Maintenance, behind its switch.
              ['/manage/maintenance', 'maintenance'], ['/manage/maintenance/:planId', 'maintenance']
            ].map(([path, tab]) => (
              <Route
                key={path}
                path={path}
                element={
                  tab === 'approvals'
                    ? (isApprover() ? <ManageHub tab="approvals" isAdmin={isAdmin()} docs={offerDocs} />
                      : <Navigate to="/my-tickets" replace />)
                    : tab === 'work-orders'
                      // Work orders are an administrator's list or a
                      // contractor's own. A member of staff has no claim on
                      // either, and /my-work-orders previously matched anyone
                      // who was not an admin - signed-out visitors included -
                      // then failed at the API. Send staff back to the
                      // tickets that are actually theirs.
                      ? (isAdmin() ? <ManageHub tab="work-orders" isAdmin={isAdmin()} docs={offerDocs} />
                        : isContractorLoggedIn() ? <Navigate to="/my-work-orders" replace />
                        : <Navigate to="/my-tickets" replace />)
                      : !isAdmin() ? <Navigate to="/my-tickets" replace />
                      : tab === 'contracts'
                        ? <FeatureRoute feature="contracts"><ManageHub tab={tab} isAdmin={isAdmin()} docs={offerDocs} catalogue={has('catalogue')} maintenance={has('maintenance')} /></FeatureRoute>
                        : tab === 'catalogue'
                          ? <FeatureRoute feature="catalogue"><ManageHub tab={tab} isAdmin={isAdmin()} docs={offerDocs} catalogue maintenance={has('maintenance')} /></FeatureRoute>
                          : tab === 'maintenance'
                            ? <FeatureRoute feature="maintenance"><ManageHub tab={tab} isAdmin={isAdmin()} docs={offerDocs} catalogue={has('catalogue')} maintenance /></FeatureRoute>
                            : <ManageHub tab={tab} isAdmin={isAdmin()} docs={offerDocs} catalogue={has('catalogue')} maintenance={has('maintenance')} />
                }
              />
            ))}
            {/* /manage/docs, the address the hub's Docs tab would have by
                the pattern of the others, goes to /docs - which for an
                administrator IS that tab, and for anybody else is their own
                way in. One address for every doc, whoever opens it. */}
            {['/manage/docs', '/manage/docs/:id'].map((path) => (
              <Route key={path} path={path} element={<DocsAlias />} />
            ))}
            <Route
              path="/audit-log"
              element={isAdmin() ? <AdminAuditLog /> : <Navigate to="/my-tickets" replace />}
            />
            {/* 115: the emergency roll-call, administrators only. */}
            <Route
              path="/roll-call"
              element={isAdmin() ? <RollCall /> : <Navigate to="/my-tickets" replace />}
            />
            {/* ANNOUNCEMENTS (125), under Advanced: administrators only, as
                the server is for writing them (routes/announcements.js). */}
            <Route
              path="/announcements"
              element={isAdmin() ? <AnnouncementsPage /> : isStaff() ? <Navigate to="/my-tickets" replace /> : <ToLogin />}
            />
            <Route
              path="/announcements/:id"
              element={isAdmin() ? <AnnouncementDetail /> : isStaff() ? <Navigate to="/my-tickets" replace /> : <ToLogin />}
            />
            {/* JOINERS & LEAVERS (113), under Advanced. The checklists are
                also a member of staff's when they own one or have a step on
                one (the server lists only those); the other two tabs are an
                administrator's. */}
            <Route
              path="/people"
              element={isAdmin() || isStaff() ? <PeoplePage view="runs" isAdmin={isAdmin()} /> : <ToLogin />}
            />
            <Route
              path="/people/runs/:id"
              element={isAdmin() || isStaff() ? <PeoplePage view="run" isAdmin={isAdmin()} /> : <ToLogin />}
            />
            {['inactive', 'away'].map((v) => (
              <Route
                key={v}
                path={`/people/${v}`}
                element={isAdmin() ? <PeoplePage view={v} isAdmin /> : isStaff() ? <Navigate to="/people" replace /> : <ToLogin />}
              />
            ))}
            {/* The install page, for everybody who is signed in by either
                door; the link is in the account menu. */}
            <Route
              path="/install"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <InstallPage />
                : <ToLogin />}
            />
            {/* Notifications (117): everybody's own, by either door; the
                link is in the account menu and the foot of every email. */}
            <Route
              path="/account/notifications"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <NotificationSettings />
                : <ToLogin />}
            />
            {/* Administrators only, and the server says so too: the whole of
                /api/chat-admin sits behind requireAdmin, and reading a
                conversation needs the firm's switch on and a reason. This
                gate is so a member of staff with the URL lands on their own
                tickets rather than on a page of refusals. */}
            <Route
              path="/chat-archive"
              element={isAdmin() ? <ChatArchive /> : <Navigate to="/my-tickets" replace />}
            />
            {/* THE PAGES BEHIND A FEATURE SWITCH (078) - recordings, meetings,
                Record in the room, both to-do pages and Links - are wrapped
                in FeatureRoute: switched off for this person, the address
                answers a calm "switched off by your administrator" page
                rather than a page of refused requests. */}
            {/* Recorded calls: everybody who is signed in by either door sees
                the ones they were on; the server decides, and ?scope=all is
                answered only for an administrator. */}
            <Route
              path="/recordings"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <FeatureRoute feature="recording"><RecordingsPage /></FeatureRoute> : <ToLogin />}
            />
            <Route
              path="/recordings/:id"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <FeatureRoute feature="recording"><RecordingDetail /></FeatureRoute> : <ToLogin />}
            />
            {/* Meetings (migration 066): everybody signed in by either door
                sees the ones they organise or are invited to; the server
                decides. A contractor lands here from the bell or an
                invitation and can answer and join, though the bar does not
                offer the page to them. */}
            <Route
              path="/meetings"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <FeatureRoute feature="meetings"><MeetingsPage /></FeatureRoute> : <ToLogin />}
            />
            <Route
              path="/meetings/:id"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <FeatureRoute feature="meetings"><MeetingPage /></FeatureRoute> : <ToLogin />}
            />
            {/* AN OUTSIDE GUEST'S WAY IN, and the one route here that asks
                for no sign-in of any kind: the token in the address is the
                whole claim, the page asks for a name, and the server hands
                back a guest bearer that works on the session routes and
                nothing else. Open to a signed-out browser on purpose - that
                is who it is for. */}
            <Route path="/join/:token" element={<GuestJoinPage />} />
            {/* Recording a meeting in the room, on the phone on the table.
                MeetingPage's "Record in the room" button lands here; given no
                meeting, the screen reads GET /meetings/:id itself. */}
            <Route
              path="/meetings/:id/record"
              element={isAdmin() || isStaff()
                ? <FeatureRoute feature={['meetings', 'recording']}><RoomRecorder /></FeatureRoute> : <ToLogin />}
            />
            {/* Administrators only. /contractors admitted isStaff() as well
                once, which is how a colleague of Gordon's reached the
                contractor list and opened a contractor's profile from it -
                reported, and fixed at the API but not here. The list is a
                hub tab now (above); the profile keeps its own page, and the
                same gate, for the case where somebody has the URL. */}
            <Route
              path="/contractor-profile/:id"
              element={isAdmin() ? <ContractorProfile /> : <Navigate to="/my-tickets" replace />}
            />
            {/* /settings/:group/:page[/:sub]#anchor, and every old ?tab= address
                redirected (pages/settings/registry.js). */}
            <Route
              path="/settings/*"
              element={isAdmin() ? <Settings /> : <Navigate to="/my-tickets" replace />}
            />

            {/* The asset register. Any signed-in member of staff may land here
                - the card on their ticket links to the device - and the server
                decides what each of them sees. */}
            {/* A contractor too, since the register learned to answer a
                portal token (both pages sign their calls with
                contractorAuth()): the portal's Assets tab opens here, and
                the server says what, if anything, they may see. */}
            {/* For a contractor, and an administrator looking through View
                as, the register is a TAB OF THE PORTAL (Gordon, 22 October:
                "when I am on the contractor's portal and I click on Assets -
                can you have the sub menu appearing below the top bar above
                Assets"): the same AssetsPage and AssetDetail, drawn in the
                portal's pane under its strip with Assets lit. Staff and
                administrators keep the standalone Asset Portal. */}
            <Route
              path="/assets"
              element={isContractorLoggedIn() || viewingContractorId()
                ? <ContractorDashboard tab="assets" assetsForContractors={contractorAssets} docsForContractors={offerDocs} />
                : isStaff() || isAdmin()
                  ? <AssetsPage /> : <ToLogin />}
            />
            {/* Lookup: scan or type any code to find what it is. */}
            <Route
              path="/assets/lookup"
              element={isStaff() || isAdmin() ? <AssetLookup /> : <ToLogin />}
            />
            <Route
              path="/assets/labels"
              element={isStaff() || isAdmin() ? <AssetLabels /> : <ToLogin />}
            />
            {/* Discovery (092): things seen but not registered, and the scan
                that finds them; and the floor plans devices are pinned on. */}
            <Route
              path="/assets/discovery"
              element={isStaff() || isAdmin() ? <AssetDiscovery /> : <ToLogin />}
            />
            {/* 30 October: plugged-in devices approved before they join the register. */}
            <Route
              path="/assets/discovery/approve"
              element={isStaff() || isAdmin() ? <AssetDiscoveryApprove /> : <ToLogin />}
            />
            {/* 095: the devices linked without a click, for IT to look over. */}
            <Route
              path="/assets/discovery/cleanup"
              element={isStaff() || isAdmin() ? <AssetDiscoveryCleanup /> : <ToLogin />}
            />
            {/* 104: shelf labels, what their QR opens, and the phone stocktake. */}
            <Route
              path="/assets/places/:locationId/labels"
              element={isStaff() || isAdmin() ? <AssetPlaceLabels /> : <ToLogin />}
            />
            <Route
              path="/assets/place/:id"
              element={isStaff() || isAdmin() ? <AssetPlace /> : <ToLogin />}
            />
            <Route
              path="/assets/stocktake/:locationId"
              element={isStaff() || isAdmin() ? <AssetStocktake /> : <ToLogin />}
            />
            <Route
              path="/assets/map"
              element={isStaff() || isAdmin() ? <AssetMap /> : <ToLogin />}
            />
            {/* 108: a desk's page, and the leavers whose desks and devices
                IT sorts out by hand. Staff and administrators. */}
            <Route
              path="/assets/desks/:id"
              element={isStaff() || isAdmin() ? <AssetDesk /> : <ToLogin />}
            />
            <Route
              path="/assets/leavers"
              element={isStaff() || isAdmin() ? <AssetLeavers /> : <ToLogin />}
            />
            {/* 119: the licences register - administrators only, as the
                server is (routes/licences.js), and behind the Licences
                switch. Staff who arrive by a link land on the devices. */}
            {[
              ['/assets/licences', <LicencesList />],
              ['/assets/licences/people/:userId', <LicencePerson />],
              ['/assets/licences/:id', <LicenceDetail />]
            ].map(([path, page]) => (
              <Route
                key={path}
                path={path}
                element={isAdmin() ? <FeatureRoute feature="licences">{page}</FeatureRoute>
                  : isStaff() ? <Navigate to="/assets" replace /> : <ToLogin />}
              />
            ))}
            {/* Devices: every machine with an agent, who has it and who is
                signed in. Staff and administrators only - never the portal. */}
            <Route
              path="/assets/devices"
              element={isStaff() || isAdmin() ? <AssetDevices /> : <Navigate to="/contractor-login" />}
            />
            {/* Fleet health (30 October): patching, unused, office totals, crashes. */}
            <Route
              path="/assets/devices/fleet"
              element={isStaff() || isAdmin() ? <AssetFleet /> : <Navigate to="/contractor-login" />}
            />
            <Route
              path="/assets/map/:locationId"
              element={isStaff() || isAdmin() ? <AssetMap /> : <ToLogin />}
            />
            <Route
              path="/assets/:id"
              element={isContractorLoggedIn() || viewingContractorId()
                ? <ContractorDashboard tab="assets" assetsForContractors={contractorAssets} docsForContractors={offerDocs} />
                : isStaff() || isAdmin()
                  ? <AssetDetail /> : <ToLogin />}
            >
              {assetTabs}
            </Route>
            <Route
              path="/my-equipment"
              element={isContractorLoggedIn() || viewingContractorId()
                /* A contractor's own list, or the one an administrator is
                   looking at through View as - never the administrator's. */
                ? <MyEquipment portal />
                : isStaff() || isAdmin() ? <MyEquipment /> : <ToLogin />}
            />
            {/* What a device's QR label opens. Open to everybody, because the
                person scanning may not be signed in yet - TagLanding sends
                them to sign in and brings them back. */}
            <Route path="/a/:tag" element={<TagLanding />} />

            {/* Staff & Admin Routes */}
            {/* /my-tickets is everybody's own tickets: a member of staff's
                list, or - for a contractor, and for an administrator looking
                through View as - the portal on its My Tickets tab. One
                address on every bar for "the tickets that are mine". */}
            <Route
              path="/my-tickets"
              element={isContractorLoggedIn() || viewingContractorId()
                ? <ContractorDashboard tab="work" assetsForContractors={contractorAssets} docsForContractors={offerDocs} />
                : isStaff() || isAdmin() ? <UserDashboard />
                : <ToLogin />}
            />
            {/* One form behind one address, in two frames. A contractor on a
                portal token, or an administrator looking through View as at
                a contractor, gets the standard form in the portal's frame -
                the strip above it, as Assets and Docs - shaped for a request
                to the firm and filed under the contractor
                (ContractorCreateTicket, 26 October: "why don't we have the
                standard ticket here?"). Staff and administrators otherwise
                get it on a page of its own, View as a member of staff
                included - their own token carries the POST. The contractor
                check comes first because an administrator viewing as a
                contractor is still isAdmin(). */}
            <Route
              path="/create"
              element={isContractorLoggedIn() || viewingContractorId()
                ? <ContractorCreateTicket assetsForContractors={contractorAssets} docsForContractors={offerDocs} />
                : isStaff() || isAdmin() ? <CreateTicket />
                : <ToLogin />}
            />
            {/* The tickets a contractor RAISED, and only those (Gordon, 22
                October): the bar's Tickets ▾ My Requests. An administrator
                looking through View as sees the contractor's. Staff and
                administrators otherwise have no requests of this kind - their
                own tickets are at /my-tickets - so they are sent there. */}
            {/* My requests for STAFF since 124: what they asked for from
                Request something, with each one's state and checklist
                (pages/catalogue/MyRequests). A contractor - and an
                administrator viewing as one - keeps exactly the page above.
                With the catalogue switched off, staff go to My tickets as
                before. */}
            <Route
              path="/my-requests"
              element={isContractorLoggedIn() || viewingContractorId()
                ? <ContractorRequests />
                : isStaff() || isAdmin() ? (has('catalogue') ? <MyRequests /> : <Navigate to="/my-tickets" replace />)
                : <ToLogin />}
            />
            {/* REQUEST SOMETHING (124): the catalogue's cards, and one
                item's form. A contractor (or an administrator viewing as
                one) gets it in the portal's frame, as Create ticket. */}
            {['/request', '/request/:itemId'].map((path) => (
              <Route
                key={path}
                path={path}
                element={isContractorLoggedIn() || viewingContractorId()
                  ? <FeatureRoute feature="catalogue"><ContractorDashboard tab="request" assetsForContractors={contractorAssets} docsForContractors={offerDocs} /></FeatureRoute>
                  : isStaff() || isAdmin() ? <FeatureRoute feature="catalogue"><RequestCatalogue /></FeatureRoute>
                  : <ToLogin />}
              />
            ))}
            <Route
              path="/tickets/:id"
              element={isStaff() || isAdmin() ? <TicketDetail />
                /* A contractor following a ticket link - from the portal, or
                   from Work & time, Billing or Issued Bills - used to land on
                   the login screen. They get their own page now rather than
                   the staff one with its controls removed. */
                : isContractorLoggedIn() ? <ToContractorTicket />
                : <ToLogin />}
            >
              {ticketTabs}
            </Route>
            {/* The contractor's own page for one ticket - and, for an
                administrator looking through View as, the same page: the
                portal's Billing lines and board link here, and "see what
                they see" (the comment on the bar above) means the
                contractor's page, not the staff one with its controls. The
                API admits an administrator naming the contractor, as on
                every other portal page; it decides what they may do. A
                member of staff or an administrator viewing as nobody has no
                contractor ticket to see and goes to their own list. */}
            <Route
              path="/my-tickets/:id"
              element={isContractorLoggedIn() || viewingContractorId() ? <ContractorTicket />
                : isAdmin() || isStaff() ? <Navigate to="/my-tickets" replace />
                : <ToLogin />}
            >
              {ticketTabs}
            </Route>

            {/* Contractor Portal Routes */}
            {/* One front door with two ways through it: Microsoft for staff,
                an emailed code for contractors. /contractor-login kept as a
                redirect so old bookmarks and the links already sent out do
                not dead-end. */}
            <Route
              path="/login"
              element={isContractorLoggedIn() ? <Navigate to="/contractor-dashboard" replace />
                : isAdmin() ? <Navigate to="/" replace />
                : isStaff() ? <Navigate to="/my-tickets" replace />
                : <Login />}
            />
            <Route
              path="/contractor-login"
              element={isContractorLoggedIn() ? <Navigate to="/contractor-dashboard" replace />
                : <Navigate to="/login" replace />}
            />
            <Route
              path="/contractor-dashboard"
              element={
                // The same question every sibling route asks, spelled the
                // same way (lib/viewAs viewingContractorId): this route used
                // to combine the gated and the ungated reads and so was
                // decided differently from /my-tickets on the first render
                // of a reload. The API still refuses the data to a non-admin.
                isContractorLoggedIn() || viewingContractorId()
                  ? <ContractorDashboard assetsForContractors={contractorAssets} docsForContractors={offerDocs} />
                  : <ToLogin />
              }
            />

            {/* Work Orders Routes. The administrator's list is a hub tab
                (/work-orders, above). The contractor's own is a portal tab
                now, and this address opens the portal on it. */}
            <Route
              path="/my-work-orders"
              // An administrator viewing as a contractor gets the contractor's
              // own list rather than being redirected to the firm-wide one:
              // /my-work-orders already serves an administrator who names a
              // contractor (routes/work-orders.js), so this is the page that
              // answers "what does Marco see", which is the question asked.
              element={isContractorLoggedIn() || viewingContractorId()
                ? <ContractorDashboard tab="myorders" assetsForContractors={contractorAssets} docsForContractors={offerDocs} />
                : isAdmin() ? <Navigate to="/work-orders" replace />
                : <Navigate to="/my-tickets" replace />}
            />
            {/* The contractor's standing availability, on its own page now
                that it is in the navbar rather than a portal tab. A
                contractor's, and nobody else's: an administrator edits
                somebody's availability from that contractor's profile, where
                it is clear whose it is. */}
            {/* The contractor's to-do list, at /todo as it always was; an
                administrator looking through View as sees theirs here too.
                Everybody else has a list of their own now, at /todos, and
                is sent there from this one. */}
            <Route
              path="/todo"
              element={isContractorLoggedIn() || viewingContractorId()
                ? <FeatureRoute feature="todos"><ContractorTodoPage /></FeatureRoute>
                : isStaff() || isAdmin() ? <Navigate to="/todos" replace />
                : <Navigate to="/my-tickets" replace />}
            />
            {/* The Emails page of the to-do list (106): a link or the bell
                lands here; the list itself opens the view from ?view=. */}
            <Route path="/todos/emails" element={<Navigate to="/todos?view=emails" replace />} />
            <Route path="/todo/emails" element={<Navigate to="/todo?view=emails" replace />} />
            {/* Staff and administrators: their own list. View as a
                contractor wins, so the bar's "To do" and a typed URL agree
                on whose list is on screen. */}
            <Route
              path="/todos"
              element={viewingContractorId() ? <Navigate to="/todo" replace />
                : isStaff() || isAdmin() ? <FeatureRoute feature="todos"><StaffTodoPage /></FeatureRoute>
                : isContractorLoggedIn() ? <Navigate to="/todo" replace />
                : <ToLogin />}
            />
            {/* Links, for everybody signed in: staff and administrators
                read and change it, a contractor (or an administrator
                looking through View as) reads it. */}
            <Route
              path="/links"
              element={isStaff() || isAdmin() || isContractorLoggedIn() || viewingContractorId()
                ? <FeatureRoute feature="links"><LinksPage /></FeatureRoute>
                : <ToLogin />}
            />
            {/* Documentation (083), for everybody signed in by either
                door; what is in it is the server's answer for this person.
                One address in three frames, the way /assets is: a
                contractor (or an administrator looking through View as) gets
                the portal on its Docs tab, an administrator the Ticket Mgmt
                hub on its Docs tab, a member of staff the page on its own
                (reached from Tickets). Every link inside the docs - the
                bell's and a ticket's too - is /docs/:id, so it stays in
                whichever frame it was opened in. */}
            {/* Help (123), for staff and administrators (a contractor's
                portal has no Help); behind Help's own switch, as the
                server is. What is in it is the server's answer. */}
            {[['/help', 'home'], ['/help/admin', 'admin'], ['/help/:id', 'article']].map(([path, view]) => (
              <Route
                key={path}
                path={path}
                element={(isAdmin() || isStaff()) && !viewingContractorId()
                  ? <FeatureRoute feature="help"><HelpPage view={view} /></FeatureRoute>
                  : <ToLogin />}
              />
            ))}
            {['/docs', '/docs/:id'].map((path) => (
              <Route
                key={path}
                path={path}
                element={isContractorLoggedIn() || viewingContractorId()
                  ? <FeatureRoute feature="docs"><ContractorDashboard tab="docs" assetsForContractors={contractorAssets} docsForContractors={offerDocs} /></FeatureRoute>
                  : isAdmin()
                    ? <FeatureRoute feature="docs"><ManageHub tab="docs" isAdmin={isAdmin()} docs={offerDocs} /></FeatureRoute>
                    : isStaff()
                      ? <FeatureRoute feature="docs"><DocsPage /></FeatureRoute>
                      : <ToLogin />}
              />
            ))}
            <Route
              path="/availability"
              element={isContractorLoggedIn() || viewingContractorId()
                ? <ContractorAvailabilityPage />
                // An administrator who is NOT viewing as somebody still goes
                // to the contractor list: "whose availability" has no answer
                // outside a view-as, and the profile page is where an
                // administrator edits it with the name on screen.
                : isAdmin() ? <Navigate to="/contractors" replace />
                : <Navigate to="/my-tickets" replace />}
            />
            {/* One work order. An administrator's, a contractor's own - and
                a member of staff who follows the link from a ticket's Work
                tab: they were bounced to My tickets without a word, which
                read as the link being broken. The API decides whether this
                person may read this order; the page says so if not. */}
            <Route
              path="/work-orders/:id"
              // Work orders are between the firm's administrators and the
              // contractor doing the job. Staff see the work on the ticket's
              // Work & costs tab instead, so a staff member is sent home
              // (e2e/staff-view.mjs pins this); nobody signed in goes to sign in.
              element={isAdmin() || isContractorLoggedIn()
                ? <WorkOrderDetail isAdmin={isAdmin()} />
                : isStaff() ? <Navigate to="/my-tickets" replace /> : <ToLogin />}
            />

            {/* The chat screen: every conversation in one place.
                //
                // Open to staff, administrators AND contractors, because the
                // point is the two sides of a ticket talking. Who appears in
                // anybody's list is the server's decision, not this route's:
                // you see the people you share a ticket with, and an
                // administrator sees everyone. A contractor landing here
                // therefore gets their own small list rather than a directory
                // of the firm. */}
            <Route
              path="/chat"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <ChatPage />
                : <Navigate to="/" replace />}
            />
            <Route
              path="/chat/:kind/:id"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <ChatPage />
                : <Navigate to="/" replace />}
            />

            {/* The live session: screen share, voice and chat.
                //
                // THREE PATHS INTO ONE PAGE, and the shapes are not
                // interchangeable. /sessions/a/:appointmentId is the link
                // inside a calendar invitation - it names the APPOINTMENT
                // because it was emailed weeks earlier and the session is
                // created on the day by whoever arrives first. The other two
                // are for a session that already exists, or one started from
                // a ticket there and then.
                //
                // Open to staff, administrators AND contractors, because the
                // whole point is the two of them in the same room. A session
                // route is not a permission: routes/sessions.js applies the
                // same rule as reading the ticket, so a forwarded invitation
                // gets a 404 rather than a way in. */}
            <Route
              path="/sessions/a/:appointmentId"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <SessionPage />
                : <Navigate to="/" replace />}
            />
            <Route
              path="/sessions/:sessionId"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <SessionPage />
                : <Navigate to="/" replace />}
            />
            <Route
              path="/tickets/:ticketId/session"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <SessionPage />
                : <Navigate to="/" replace />}
            />
            {/* A fourth path in: a call on the General thread with one
                person, with no ticket. Only works when the firm has turned
                on "Calls may be made without a ticket" in Settings; the
                server refuses otherwise and the page shows the reason. */}
            <Route
              path="/chat/:kind/:id/session"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <SessionPage />
                : <Navigate to="/" replace />}
            />

            {/* NOTHING AT THIS ADDRESS. Every unknown URL used to be
                redirected home without a word, so a mistyped or stale deep
                link gave somebody their ticket list and no explanation.
                Somebody not signed in at all still goes to the front door -
                the address may well be a real one they are not yet allowed
                to see, and it is remembered for after. */}
            <Route
              path="*"
              element={isAdmin() || isStaff() || isContractorLoggedIn()
                ? <NotFound home={isAdmin() ? '/manage' : viewingPortal() ? '/contractor-dashboard' : '/my-tickets'}
                            homeLabel={isAdmin() ? 'Go to Ticket management' : viewingPortal() ? 'Go to the portal' : 'Go to my tickets'} />
                : <ToLogin />}
            />
          </Routes>
        )}
    </div>
  );
}

function App() {
  /*
   * SWITCHED OFF BY AN ADMINISTRATOR (lib/blocked): the whole shell goes, not
   * just the page. Unmounting it stops every poll, the chat, the bell and the
   * calls with it, so nothing keeps asking a server that will only refuse -
   * and no route can send them to the sign-in page, where the quiet Microsoft
   * sign-in would only bring them straight back here.
   */
  const blocked = useBlocked();
  return (
    <ErrorBoundary>
      {blocked ? <AccessSwitchedOff /> : <RouterProvider router={router} />}
    </ErrorBoundary>
  );
}

/*
 * A DATA ROUTER WITH ONE ROUTE, which is the whole shell. The routes
 * themselves stay as the <Routes> inside AppShell, with their gates read
 * fresh on every render (see the note above AppShell) - nothing about them
 * changed. What the data router adds is that navigation can be HELD: the
 * unsaved-changes guard on the long forms (lib/useUnsavedChanges, useBlocker)
 * needs it, and a plain <BrowserRouter> has no such thing. Made once, at
 * module level, as the router must be.
 */
const router = createBrowserRouter([{ path: '*', element: <AppShell /> }]);

export default App;
